'use client';

import React, { memo, useEffect, useState } from 'react';

import RichText from '@/components/ui/RichText/RichText';
import { COMPONENT_STYLES } from '@/constants/styles';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  StatisticComponent,
  StatisticsMetricsProps,
} from '@/types/statisticsMetrics';

const useCountUp = (endValue: number, shouldStart: boolean) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (shouldStart && endValue > 0) {
      const duration = 2000;
      const frameDuration = 1000 / 60; // 60fps
      const totalFrames = Math.round(duration / frameDuration);
      let frame = 0;

      const timer = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        // Ease out quad formula
        const currentCount = Math.floor(endValue * (progress * (2 - progress)));

        if (frame <= totalFrames) {
          setCount(currentCount);
        } else {
          setCount(endValue);
          clearInterval(timer);
        }
      }, frameDuration);

      return () => clearInterval(timer);
    }
  }, [endValue, shouldStart]);

  return count;
};

const extractNumber = (value: string): number => {
  const match = value.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

const StatisticCard: React.FC<{
  item: StatisticComponent;
  shouldAnimate: boolean;
}> = memo(({ item, shouldAnimate }) => {
  const { mainValue = '', label = '' } = item;

  const originalNumber = extractNumber(mainValue);
  const animatedNumber = useCountUp(originalNumber, shouldAnimate);

  const displayValue =
    animatedNumber > 0
      ? mainValue.replace(/\d+/, animatedNumber.toString())
      : mainValue;

  return (
    <div
      className={`relative z-40 ${COMPONENT_STYLES.CARD.GLASS} flex min-h-[120px] flex-col justify-center rounded-xl p-4 text-center shadow-lg transition-transform duration-300 hover:scale-105 sm:-mt-20 sm:min-h-[140px] sm:p-6 md:-mt-24`}
    >
      <div className="mx-auto mb-2 flex h-10 items-center justify-center tabular-nums sm:h-12">
        <div className="text-theme-primary text-2xl leading-tight font-bold sm:text-3xl">
          {displayValue}
        </div>
      </div>
      <div className="text-theme-primary-dark text-xs font-medium tracking-wide uppercase sm:text-sm">
        {label}
      </div>
    </div>
  );
});

StatisticCard.displayName = 'StatisticCard';

const StatisticsMetrics: React.FC<StatisticsMetricsProps> = memo(({ data }) => {
  const { ref, sectionClasses, childClasses, getChildStyle, isVisible } =
    useScrollAnimation({ threshold: 0.3 });

  if (!data?.componentsCollection?.items?.length) return null;

  return (
    <section
      ref={ref}
      className={`relative z-30 py-6 sm:py-8 md:py-12 ${sectionClasses}`}
      data-testid="statistics-metrics"
    >
      <div className="container mx-auto max-w-6xl px-4">
        {data.heading?.raw && (
          <div
            className={`mb-10 text-center ${childClasses()}`}
            style={getChildStyle(200)}
          >
            <RichText
              styles={{
                p: 'text-xl sm:text-3xl lg:text-4xl font-bold text-theme-brand-blue',
              }}
            >
              {data.heading.raw}
            </RichText>
          </div>
        )}

        <div className="xs:grid-cols-2 grid grid-cols-1 gap-4 md:grid-cols-4 md:gap-8">
          {data.componentsCollection.items.map((item) => (
            <div
              key={item.sys.id}
              className={`mb-10 text-center ${childClasses()}`}
              style={getChildStyle(200)}
            >
              {data.heading?.raw && (
                <RichText
                  styles={{
                    p: 'text-xl sm:text-3xl lg:text-4xl font-bold text-gray-800',
                  }}
                >
                  {data.heading.raw}
                </RichText>
              )}
            </div>
          ))}

          <div className="xs:grid-cols-2 grid grid-cols-1 gap-4 md:grid-cols-4 md:gap-8">
            {data.componentsCollection.items.map((item, index) => (
              <div
                key={item.sys.id}
                className={childClasses()}
                style={getChildStyle(400 + index * 100)}
              >
                <StatisticCard item={item} shouldAnimate={isVisible} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

StatisticsMetrics.displayName = 'StatisticsMetrics';

export default StatisticsMetrics;
